"use client";

import React from "react";
import { Activity, TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, Gauge, CircleQuestionMark as HelpCircle, PhoneCall, ShieldAlert, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SymptomItem {
  id: string;
  title: string;
  badge: string;
  badgeVariant: "destructive" | "default" | "secondary" | "outline";
  iconName: "low" | "fluctuate" | "hotOnly" | "high" | "hammer" | "discolored";
  description: string;
  possibleCauses: string;
  actionText: string;
  actionLink: string;
}
const symptomsList: SymptomItem[] = [{
  id: "low-pressure-all",
  title: "Low Pressure Throughout House",
  badge: "Common Issue",
  badgeVariant: "secondary",
  iconName: "low",
  description: "Water barely trickles from faucets and showerheads across every fixture simultaneously.",
  possibleCauses: "Main shutoff valve partially closed, hidden main line leak, or failing pressure regulator.",
  actionText: "Inspect for hidden leaks",
  actionLink: "/leak-detection-repair/"
}, {
  id: "high-water-pressure",
  title: "Excessive High Water Pressure",
  badge: "Urgent Risk",
  badgeVariant: "destructive",
  iconName: "high",
  description: "Water slams hard against fixtures, causing leaks, stressing joints, and risking appliance burst.",
  possibleCauses: "Failed pressure reducing valve (PRV) or municipal pressure surge exceeding 80 PSI.",
  actionText: "Fix pipe stress & PRV",
  actionLink: "/pipe-repair/"
}, {
  id: "hot-water-pressure-only",
  title: "Low Hot Water Pressure Only",
  badge: "Water Heater Issue",
  badgeVariant: "default",
  iconName: "hotOnly",
  description: "Cold water flows normally, but hot water pressure drops significantly at every faucet.",
  possibleCauses: "Heavy sediment accumulation in water tank or corroded hot water outlet valves.",
  actionText: "Water heater repair service",
  actionLink: "/water-heater-repair/"
}, {
  id: "sudden-pressure-fluctuations",
  title: "Sudden Pressure Drops & Spikes",
  badge: "Warning Sign",
  badgeVariant: "outline",
  iconName: "fluctuate",
  description: "Water pressure jumps erratically when another fixture, toilet, or appliance is engaged.",
  possibleCauses: "Corroded undersized pipes, mineral scaling, or failing pressure regulator.",
  actionText: "Explore whole-house repiping",
  actionLink: "/repiping/"
}, {
  id: "water-hammer-noises",
  title: "Banging Pipes & Water Hammer",
  badge: "Pipe Hazard",
  badgeVariant: "destructive",
  iconName: "hammer",
  description: "Loud thuds, vibrations, or rattling noises in the walls immediately after turning off a tap.",
  possibleCauses: "High incoming pressure, loose pipe strapping, or depleted air chambers.",
  actionText: "Emergency pipe evaluation",
  actionLink: "/emergency-plumbing/"
}, {
  id: "discolored-trickle",
  title: "Rusty or Discolored Low Flow",
  badge: "Pipe Corrosion",
  badgeVariant: "secondary",
  iconName: "discolored",
  description: "Weak flow paired with brownish, yellow, or cloudy water from household faucets.",
  possibleCauses: "Severe interior rust in aging galvanized iron supply pipes or city line disruption.",
  actionText: "View plumbing solutions",
  actionLink: "/problems/"
}];
export default function PressureSymptoms() {
  const shouldReduceMotion = useReducedMotion();
  const getSymptomIcon = (iconType: SymptomItem["iconName"]) => {
    switch (iconType) {
      case "low":
        return <Gauge className="size-6 text-primary" />;
      case "high":
        return <AlertTriangle className="size-6 text-destructive" />;
      case "hotOnly":
        return <Flame className="size-6 text-accent-foreground" />;
      case "fluctuate":
        return <Activity className="size-6 text-primary" />;
      case "hammer":
        return <ShieldAlert className="size-6 text-destructive" />;
      case "discolored":
        return <Droplets className="size-6 text-primary" />;
      default:
        return <Wrench className="size-6 text-primary" />;
    }
  };
  return <section id="waterpressuresymptoms" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold uppercase tracking-wider text-xs">
            Diagnostic Guide
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Identify Your Water Pressure Symptoms
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Abnormal water pressure is often an early indicator of hidden leaks, failing valves, or pipe corrosion. Review common signs below to find the right repair solution for your home in Jonesboro.
          </p>
        </div>

        {/* Symptoms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {symptomsList.map((symptom, index) => <motion.div key={symptom.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : index * 0.08
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="p-2.5 rounded-lg bg-muted inline-flex items-center justify-center">
                        {getSymptomIcon(symptom.iconName)}
                      </div>
                      <Badge variant={symptom.badgeVariant}>
                        {symptom.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-default font-semibold text-card-foreground">
                      {symptom.title}
                    </CardTitle>
                    <CardDescription className="text-sm font-default text-muted-foreground pt-1">
                      {symptom.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="p-3.5 rounded-md bg-muted/60 border border-border text-xs leading-relaxed">
                      <span className="font-semibold text-foreground flex items-center gap-1.5 mb-1">
                        <HelpCircle className="size-3.5 text-muted-foreground" /> Likely Causes:
                      </span>
                      <p className="text-muted-foreground">
                        {symptom.possibleCauses}
                      </p>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-2">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={symptom.actionLink} className="w-full flex items-center justify-between">
                      <span className="font-medium text-sm">{symptom.actionText}</span>
                      <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Emergency Callout Box */}
        <div className="mt-12 md:mt-16 rounded-xl bg-primary text-primary-foreground p-6 sm:p-8 md:p-10 shadow-md">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left">
              <h3 className="text-2xl font-default font-bold text-primary-foreground">
                Need Fast Help With Extreme Water Pressure?
              </h3>
              <p className="text-primary-foreground/90 text-sm sm:text-base max-w-2xl">
                Unchecked high pressure or sudden zero pressure can signal burst lines and cause major property damage. Our emergency plumbers in Jonesboro are on call 24/7.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-semibold">
                <Link to="/contact/">
                  <PhoneCall className="size-4" />
                  Request Emergency Service
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 border-primary-foreground/30">
                <Link to="/problems/">
                  View All Problems
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}